import { AsyncPipe, DecimalPipe } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    inject
} from "@angular/core";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import _ from "lodash";
import { Observable, Subject, combineLatest, merge } from "rxjs";
import { map, scan, takeUntil } from "rxjs/operators";
import { ILeverantor } from "~app/features/virkeskop/virkesleverantor/models/virkesleverantorModel";
import { Kontrakt } from "../../models/kontrakt-models";
import { KontraktSkogsvardBetalning } from "../../models/skogsvard/skogsvard-models";
import { BetalningMediatorService } from "../../services/betalning-mediator.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { LeverantorMediatorService } from "../../services/leverantor-mediator.service";
import { SkogsvardBetalningMediatorService } from "../../services/skogsvard-betalning-mediator.service";
import { SkogsvardService } from "../../services/skogsvard.service";
import { BetalningLeverantorComponent } from "./betalning-leverantor/betalning-leverantor.component";
import { BetalningSkogsvardComponent } from "./betalning-skogsvard/betalning-skogsvard.component";

interface IKontraktBetalningVM{
    betalningTotal: number;
    kontraktId: number;
    kontraktsbelopp: number;
    kontraktsbeloppPercentage: number;
    leverantorer: ILeverantor[];
    ranta?: boolean;
}

@Component({
    selector: "rask-kontrakt-betalning",
    templateUrl: "./kontrakt-betalning.component.html",
    styleUrls: ["./kontrakt-betalning.component.scss"],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TogglePanelComponent,
        TextmultilineComponent,
        BetalningLeverantorComponent,
        BetalningSkogsvardComponent,
        AsyncPipe,
        DecimalPipe
    ]
})
export class KontraktBetalningComponent implements OnInit, OnDestroy {
    protected totalkonstnadSkogsvard?: number;
    protected virkeskopsKontrakt: Kontrakt;
    protected vm$: Observable<IKontraktBetalningVM>;

    private readonly betalningMediatorService = inject(BetalningMediatorService);
    private readonly initialState: IKontraktBetalningVM = {
        kontraktId: 0,
        betalningTotal: 0,
        kontraktsbeloppPercentage: 0,
        kontraktsbelopp: 0,
        leverantorer: []
    };
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly leverantorMediatorService = inject(LeverantorMediatorService);
    private readonly ngDestroyed$ = new Subject<void>();
    private readonly skogsvardBetalningMediatorService = inject(SkogsvardBetalningMediatorService);
    private readonly skogsvardService = inject(SkogsvardService);

    ngOnDestroy(): void {
        this.ngDestroyed$.next();
        this.ngDestroyed$.complete();
    }

    ngOnInit(): void {
        const kontraktsbelopp$ = this.betalningMediatorService.kontraktsbelopp$.pipe(
            map(belopp => (vm:  IKontraktBetalningVM) => ({
                ...vm,
                kontraktsbelopp: belopp
            })));

        const leverantorer$ = this.leverantorMediatorService.leverantorer$.pipe(
            map(leverantorer => (vm:  IKontraktBetalningVM) => ({
                ...vm,
                leverantorer: leverantorer
            })));

        const kontrakt$ = this.kontraktMediatorService.kontrakt$.pipe(
            map(kontrakt => (vm:  IKontraktBetalningVM) => ({
                ...vm,
                ranta: kontrakt.ranta,
                kontraktId: kontrakt.id
            })));

        this.kontraktMediatorService.kontrakt$.pipe(
            takeUntil(this.ngDestroyed$)
        ).subscribe(k => this.virkeskopsKontrakt = k);

        const recalculate$ = combineLatest([
            this.betalningMediatorService.betalningar$,
            this.betalningMediatorService.kontraktsbelopp$,
            leverantorer$,
            kontrakt$
        ]).pipe(
            map(([
                betalningar,
                kontraktsbelopp
            ]) => (vm: IKontraktBetalningVM) => {
                const betalningTotal = betalningar.map(b => b.belopp).reduce((a,b) => a+b,0);
                let betalningProcent = 0;
                if(kontraktsbelopp > 0) {
                    betalningProcent = (betalningTotal / kontraktsbelopp) * 100;
                }
                return{
                    ...vm,
                    betalningTotal: betalningTotal,
                    kontraktsbeloppPercentage: betalningProcent
                };
            }));

        this.vm$ = merge(
            kontrakt$,
            leverantorer$,
            kontraktsbelopp$,
            recalculate$)
            .pipe(
                scan((
                    vm: IKontraktBetalningVM,
                    mutatorFn: (vm: IKontraktBetalningVM) => IKontraktBetalningVM) => mutatorFn(vm),
                this.initialState));

        this.skogsvardBetalningMediatorService.skogsvardBetalning$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe( skogsvardBetalningar => {
                if (skogsvardBetalningar?.length > 0) {
                    const totalCalculated = skogsvardBetalningar.filter(x => !this.shouldHandleBelopp(x))
                        .reduce((ty, u) => ty + (u.apris * u.kvantitet), 0);
                    const totalBelopp = skogsvardBetalningar.filter(x => this.shouldHandleBelopp(x)).reduce((ty, u) => ty + u.belopp, 0);
                    const total = totalCalculated + totalBelopp;
                    this.totalkonstnadSkogsvard = _.round(total, 0);

                } else {
                    this.totalkonstnadSkogsvard = 0;
                }
            });
    }

    protected trackByFn(index: number, item: ILeverantor) {
        return item.id;
    }

    private shouldHandleBelopp(betalning: KontraktSkogsvardBetalning): boolean {
        return this.skogsvardService.shouldHandleBelopp(betalning);
    }
}
